import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scheduleData, setScheduleData] = useState(() => {
    const savedData = localStorage.getItem('scheduleData');
    return savedData ? JSON.parse(savedData) : {
      coachHashedId: null,
      month: null,
    };
  });

  // 新增：定時刷新相關狀態
  const [refreshInterval, setRefreshInterval] = useState(null);

  useEffect(() => {
    localStorage.setItem('scheduleData', JSON.stringify(scheduleData));
  }, [scheduleData]);

  // 新增：定時刷新令牌的函數
  const refreshTokenIfNeeded = async () => {
    try {
      const token = localStorage.getItem('token');
      const refresh = localStorage.getItem('refresh');
      
      if (!token || !refresh) {
        return false;
      }

      // 檢查令牌是否即將過期（提前5分鐘刷新）
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      const tokenExpiry = tokenPayload.exp;
      const timeUntilExpiry = tokenExpiry - currentTime;

      // 如果令牌在5分鐘內過期，則刷新
      if (timeUntilExpiry < 300) { // 300秒 = 5分鐘
        console.log('Token expiring soon, refreshing...');
        const response = await api.post('/token/refresh/', { refresh });
        const newAccess = response.data?.access;
        
        if (newAccess) {
          localStorage.setItem('token', newAccess);
          api.defaults.headers.common['Authorization'] = `Bearer ${newAccess}`;
          console.log('Token refreshed successfully');
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      // 刷新失敗，清理並登出
      logout();
      return false;
    }
  };

  // 新增：設置定時刷新
  const setupTokenRefresh = () => {
    // 清除現有的定時器
    if (refreshInterval) {
      clearInterval(refreshInterval);
    }

    // 每5分鐘檢查一次令牌
    const interval = setInterval(async () => {
      await refreshTokenIfNeeded();
    }, 5 * 60 * 1000); // 5分鐘 = 5 * 60 * 1000 毫秒

    setRefreshInterval(interval);
  };

  // 新增：清除定時刷新
  const clearTokenRefresh = () => {
    if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
  };

  useEffect(() => {
    const initAuth = () => {
      try {
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('token');
        const refresh = localStorage.getItem('refresh');
        
        if (storedUser && token) {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // 設置定時刷新
          setupTokenRefresh();
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('refresh');
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // 組件卸載時清除定時器
    return () => {
      clearTokenRefresh();
    };
  }, []);

  const login = async (credentials) => {
    try {
      const response = await api.post('/login/', credentials);
      const { access, refresh, user: userData } = response.data;
      
      localStorage.setItem('token', access);
      if (refresh) {
        localStorage.setItem('refresh', refresh);
      }
      localStorage.setItem('user', JSON.stringify(userData));
      
      api.defaults.headers.common['Authorization'] = `Bearer ${access}`;
      
      setUser(userData);
      
      // 登入成功後設置定時刷新
      setupTokenRefresh();
      
      return userData;
    } catch (error) {
      console.error('Login error:', error);
      throw error.response?.data || error.message;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    
    // 清除定時刷新
    clearTokenRefresh();
    
    setUser(null);
  };

  const register = async (formData) => {
    try {
      const response = await api.post(
        `/register/`,
        formData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      // 不要自動登入，因為需要等待郵件驗證
      return response.data;
    } catch (error) {
      if (error.response) {
        // 處理後端返回的錯誤訊息
        throw error.response.data;
      }
      throw new Error('註冊時發生錯誤');
    }
  };

  const verifyEmail = async (uidb64, token) => {
    try {
      const response = await api.get(
        `/verify-email/${uidb64}/${token}/`
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      }
      throw new Error('驗證郵件時發生錯誤');
    }
  };

  const refreshUserInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await api.get('/profile/');
      const userData = response.data;
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Error refreshing user info:', error);
      if (error.response?.status === 401) {
        logout();
      }
    }
  };

  const userlist = async () => {
    const response = await api.get('/users/');
    return response.data;
  };

  const value = {
    user,
    loading,
    scheduleData,
    setScheduleData,
    login,
    logout,
    register,
    verifyEmail,
    refreshUserInfo,
    userlist,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 